import React, { useState, useRef, useEffect } from "react";
import ChevronIcon from "./SVGIcons/ChevronIcon";
import ClearIcon from "./SVGIcons/ClearIcon";

const TimelineSelector = ({ onChange, dropdownTitle, initialStateThisWeek, value }) => {
  const options = [
    "All Time",
    "Custom Date",
    "Yesterday",
    "Today",
    "Last Week",
    "This Week",
    "Last Month",
    "This Month",
    "Last 7 Days",
    "Last 30 Days",
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(options[initialStateThisWeek ? 5 : 0]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isHoveredHeader, setIsHoveredHeader] = useState(false);
  const [isHoveredClear, setIsHoveredClear] = useState(false);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownOptionsRef = useRef(null);

  const filteredOptions = options.filter((option) => option.toLowerCase());

  const handleSelect = (value) => {
    setSelectedValue(value);
    onChange(value);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedValue(options[initialStateThisWeek ? 5 : 0]);
    onChange(options[initialStateThisWeek ? 5 : 0]);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const scrollToOption = (index) => {
    const optionsContainer = dropdownOptionsRef.current;
    if (!optionsContainer) return;

    const optionElements = optionsContainer.children;
    if (index < 0 || index >= optionElements.length) return;

    const optionElement = optionElements[index];
    const containerTop = optionsContainer.scrollTop;
    const containerBottom = containerTop + optionsContainer.clientHeight;
    const elementTop = optionElement.offsetTop;
    const elementBottom = elementTop + optionElement.clientHeight;

    if (elementTop < containerTop) {
      optionsContainer.scrollTop = elementTop;
    } else if (elementBottom > containerBottom) {
      optionsContainer.scrollTop =
        elementBottom - optionsContainer.clientHeight;
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((prevIndex) => {
          const newIndex =
            prevIndex === filteredOptions.length - 1 ? 0 : prevIndex + 1;
          scrollToOption(newIndex);
          return newIndex;
        });
        break;

      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((prevIndex) => {
          const newIndex =
            prevIndex === 0 ? filteredOptions.length - 1 : prevIndex - 1;
          scrollToOption(newIndex);
          return newIndex;
        });
        break;

      case "Enter":
        if (focusedIndex >= 0) {
          handleSelect(filteredOptions[focusedIndex]);
        }
        break;

      case "Escape":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
  if (value && value !== selectedValue) {
    setSelectedValue(value);
  }
  }, [value]);

  return (
    <div style={styles.dropdownContainer} ref={dropdownRef}>
      <div
        style={{
          ...styles.dropdownHeader,
          ...(isHoveredHeader ? styles.dropdownHeaderHover : {}),
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHoveredHeader(true)}
        onMouseLeave={() => setIsHoveredHeader(false)}
      >
        <div style={styles.dropdownContent}>
          <span style={styles.dropdownTitle}>
            {dropdownTitle || "Time Period"}
          </span>
          <span style={styles.dropdownValue}>{selectedValue}</span>
        </div>
        <div style={styles.dropdownActions}>
          {selectedValue !== options[initialStateThisWeek ? 5 : 0] && (
            <button
              onClick={handleClear}
              style={{
                ...styles.clearButton,
                ...(isHoveredClear ? styles.clearButtonHover : {}),
              }}
              onMouseEnter={() => setIsHoveredClear(true)}
              onMouseLeave={() => setIsHoveredClear(false)}
            >
              <ClearIcon />
            </button>
          )}
          <div
            style={{
              ...styles.chevronIcon,
              ...(isOpen ? styles.chevronIconRotate : {}),
            }}
          >
            <ChevronIcon />
          </div>
        </div>
      </div>

      {isOpen && (
        <div style={styles.dropdownMenu}>
          <div ref={dropdownOptionsRef} style={styles.dropdownOptions}>
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                style={{
                  ...styles.optionItem,
                  ...(focusedIndex === index ? styles.optionItemHover : {}),
                  ...(selectedValue === option
                    ? styles.optionItemSelected
                    : {}),
                }}
                onMouseEnter={() => setFocusedIndex(index)}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div style={styles.noOptions}>No matching time periods</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineSelector;

const styles = {
  dropdownContainer: {
    position: "relative",
    width: "160px",
    border: "1px solid #ddd",
  },
  dropdownHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "white",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "border-color 0.2s ease",
  },
  dropdownHeaderHover: {
    borderColor: "#999",
  },
  dropdownContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  dropdownTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
  },
  dropdownValue: {
    fontSize: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dropdownActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  clearButton: {
    padding: "2px",
    background: "none",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    width: "16px",
    height: "16px",
    marginRight: "2px",
  },
  clearButtonHover: {
    backgroundColor: "#f0f0f0",
    color: "#333",
  },
  chevronIcon: {
    transition: "transform 0.2s ease",
    display: "flex",
  },
  chevronIconRotate: {
    transform: "rotate(180deg)",
  },
  dropdownMenu: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    width: "100%",
    backgroundColor: "white",
    border: "1px solid #ddd",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    padding: "8px",
    borderBottom: "1px solid #eee",
    gap: "8px",
  },
  searchIconContainer: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "14px",
    padding: "4px",
    background: "none",
    width: "100%",
    minWidth: 0,
  },
  dropdownOptions: {
    maxHeight: "240px",
    overflowY: "auto",
  },
  optionItem: {
    padding: "8px 12px",
    fontSize: "14px",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  optionItemHover: {
    backgroundColor: "#f0f0f0",
  },
  optionItemSelected: {
    backgroundColor: "#f0f0f0",
  },
  noOptions: {
    padding: "12px",
    textAlign: "center",
    color: "#666",
    fontSize: "14px",
  },
};
